/**
 * cursor 一覧の定型（クエリ + ページャ + フィルタ変更時のリセット）。
 *
 * これを共通化しないと、画面ごとに「フィルタを変えたのに 2 ページ目のカーソルのまま」
 * という同じバグを 15 回書くことになる。
 */
import * as React from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCursorPagination } from '@mail-sv/ui';
import { api, cursorQuery, type QueryParams } from './api.js';
import type { CursorPage } from '../types/api.js';

export interface CursorListResult<T> {
  rows: T[];
  nextCursor: string | null;
  query: UseQueryResult<CursorPage<T>, unknown>;
  limit: number;
  canGoPrev: boolean;
  setLimit: (n: number) => void;
  goNext: () => void;
  goPrev: () => void;
}

export function useCursorList<T>(opts: {
  /** `/nodes` のように ops prefix 以下のパス。 */
  path: string;
  /** フィルタ + ページ位置を含めた queryKey を作る。 */
  keyFor: (params: QueryParams) => readonly unknown[];
  filters: QueryParams;
  initialLimit?: number;
  refetchInterval?: number | false;
  enabled?: boolean;
}): CursorListResult<T> {
  const pager = useCursorPagination(opts.initialLimit ?? 50);
  const { reset } = pager;

  // フィルタが変わったら先頭ページへ戻す。前のカーソルは別条件の位置を指していて意味が無い。
  const filterKey = JSON.stringify(opts.filters);
  React.useEffect(() => {
    reset();
  }, [filterKey, reset]);

  const params = cursorQuery(pager.limit, pager.cursor, opts.filters);

  const query = useQuery({
    queryKey: opts.keyFor(params),
    queryFn: () => api.get<CursorPage<T>>(opts.path, params),
    refetchInterval: opts.refetchInterval ?? false,
    enabled: opts.enabled ?? true,
    // 次ページへ移った瞬間に表が消えないよう、前ページのデータを保持する。
    placeholderData: (prev) => prev,
  });

  return {
    rows: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor ?? null,
    query,
    limit: pager.limit,
    canGoPrev: pager.canGoPrev,
    setLimit: pager.setLimit,
    goNext: () => pager.goNext(query.data?.nextCursor ?? null),
    goPrev: pager.goPrev,
  };
}
